import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { logger } from '../utils/logger';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem('theme') as Theme;
      if (saved && ['light', 'dark', 'system'].includes(saved)) {
        return saved;
      }
    } catch (e) {
      logger.warn('Failed to read theme from localStorage', e);
    }
    return 'light'; // 默认使用light而不是system，避免初始状态问题
  });

  // 计算当前解析的主题
  const getResolvedTheme = (currentTheme: Theme): 'light' | 'dark' => {
    if (currentTheme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return currentTheme;
  };

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    return getResolvedTheme(theme);
  });

  // 应用主题到HTML元素
  useEffect(() => {
    const root = document.documentElement;
    
    // 计算当前应该应用的主题
    let currentResolved: 'light' | 'dark';
    if (theme === 'system') {
      currentResolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      currentResolved = theme;
    }
    
    // 更新状态和应用类
    setResolvedTheme(currentResolved);
    root.classList.remove('light', 'dark');
    root.classList.add(currentResolved);
    
    logger.debug('Theme applied', { theme, resolvedTheme: currentResolved, htmlClass: root.className });

    // 如果是system模式，监听系统主题变化
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const updateTheme = () => {
        const systemTheme = mediaQuery.matches ? 'dark' : 'light';
        logger.debug('System theme changed', { systemTheme });
        setResolvedTheme(systemTheme);
        root.classList.remove('light', 'dark');
        root.classList.add(systemTheme);
      };
      
      // 使用现代API
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', updateTheme);
        return () => mediaQuery.removeEventListener('change', updateTheme);
      } else {
        // 降级到旧API
        mediaQuery.addListener(updateTheme);
        return () => mediaQuery.removeListener(updateTheme);
      }
    }
    
    // 如果不是system模式，清理监听器（如果存在）
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    try {
      setThemeState(newTheme);
      localStorage.setItem('theme', newTheme);
    } catch (e) {
      logger.warn('Failed to save theme to localStorage', e);
      setThemeState(newTheme);
    }
  };

  const toggleTheme = () => {
    // 计算当前应该切换到的主题
    // 如果当前是system模式，基于resolvedTheme切换
    // 否则直接基于theme切换
    const currentEffectiveTheme = theme === 'system' ? resolvedTheme : theme;
    const newTheme = currentEffectiveTheme === 'light' ? 'dark' : 'light';
    
    logger.info('Toggling theme', { 
      from: currentEffectiveTheme, 
      to: newTheme, 
      currentTheme: theme,
      resolvedTheme 
    });
    
    setTheme(newTheme);
    
    // 立即应用，不等待React状态更新
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(newTheme);
    logger.debug('Theme immediately applied to HTML', { newTheme });
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
